import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import {
  MessageWithCategoryAndType,
  groupMessagesByCategory,
} from "../../../../utils/product/getPromoMessaging.ts";
import styles from "./SpecialOffersDrawer.module.scss";
import SpecialOffersDrawerPromotion from "../SpecialOffersPromotion/SpecialOffersPromotion";
import { giftWithPurchaseTypes } from "../../../../utils/product/getPromoMessaging.ts";
import { Message } from "@/types/types";
import { SaleTagIcon } from "../../../__shared__/SaleTagIcon/SaleTagIcon";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

type SpecialOffersDrawerProps = {
  /**
   * An array of Message objects.
   */
  promotions: MessageWithCategoryAndType[];
  drawerIsOpen: boolean;
  onClose: () => void;
  onPromotionClose: () => void;
  isBlackOverride: boolean;
  prodCatEndpoint?: string;
  prodCatApiKey?: string;
  basketEndpoint?: string;
  specialOffersVariant?: string;
  applyNewDrawerUI?: boolean;
  showSideDrawerVariant?: boolean;
};

export const SpecialOffersDrawer = ({
  promotions,
  drawerIsOpen,
  onClose,
  onPromotionClose,
  isBlackOverride,
  prodCatEndpoint,
  prodCatApiKey,
  basketEndpoint,
  specialOffersVariant,
  applyNewDrawerUI = false,
  showSideDrawerVariant = false,
}: SpecialOffersDrawerProps) => {
  const { offers, services, claims } = groupMessagesByCategory(promotions);

  const freeGifts = offers.filter(
    (message: Message) =>
      message.promotionType && message.promotionType in giftWithPurchaseTypes,
  );
  const hasOneFreeGift = freeGifts.length === 1;

  const drawerStyles = cx(styles.versionThreeDrawerHeader, {
    redPromoDrawer: !isBlackOverride,
  });

  const drawerHeading =
    specialOffersVariant === "A" || specialOffersVariant === "B"
      ? promotions.length === 1
        ? "Offer"
        : "Offers"
      : promotions.length === 1
        ? "Special offer"
        : "Special offers";

  const offersAndServicesCount = offers.length + services.length;
  const claimsCount = claims.length;

  return (
    <Drawer
      isOpen={drawerIsOpen}
      onClose={onClose}
      heading={drawerHeading}
      className={drawerStyles}
    >
      {offersAndServicesCount > 0 && (
        <section
          className={cx(
            styles.categorySection,
            applyNewDrawerUI && styles.noBorder,
          )}
        >
          {!showSideDrawerVariant && (
            <div className={styles.categoryTitle}>
              {!applyNewDrawerUI && specialOffersVariant !== "A" && (
                <SaleTagIcon type="medium" isBlackOverride={isBlackOverride} />
              )}
              <h2
                className={cx(
                  styles.title,
                  isBlackOverride && styles["blackOverride"],
                )}
              >
                {offersAndServicesCount === 1
                  ? "Offer available with this purchase"
                  : "Offers available with this purchase"}
              </h2>
            </div>
          )}
          {offers.length > 0 ? (
            <div className={styles.specialOffersNote}>
              Please note: you can normally only use one promo code per order.
              If an offer states so, you can use one code per qualifying product
              (up to a maximum of 10 codes per order). Where a specific product
              offer states so, a free delivery code can also be added. For full
              terms and exclusions, please see the offers terms below.
            </div>
          ) : null}
          {offers.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              productHasOneFreeGift={hasOneFreeGift}
              prodCatEndpoint={prodCatEndpoint}
              prodCatApiKey={prodCatApiKey}
              basketEndpoint={basketEndpoint}
              specialOffersVariant={specialOffersVariant}
              applyNewDrawerUI={applyNewDrawerUI}
              showSideDrawerVariant={showSideDrawerVariant}
            />
          ))}
          {services.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              specialOffersVariant={specialOffersVariant}
              applyNewDrawerUI={applyNewDrawerUI}
              showSideDrawerVariant={showSideDrawerVariant}
            />
          ))}
        </section>
      )}
      {claimsCount > 0 && (
        <section
          className={cx(
            styles.categorySection,
            applyNewDrawerUI && styles.noBorder,
          )}
        >
          <div className={styles.categoryTitle}>
            {!applyNewDrawerUI &&
              specialOffersVariant !== "A" &&
              !showSideDrawerVariant && (
                <SaleTagIcon type="medium" isBlackOverride={isBlackOverride} />
              )}
            <h2
              className={cx(
                styles.title,
                isBlackOverride && styles["blackOverride"],
              )}
            >
              {claimsCount === 1
                ? "Claim this offer once you’ve made your purchase"
                : "Claim these offers once you’ve made your purchase"}
            </h2>
          </div>
          {claims.map((promotion, index) => (
            <SpecialOffersDrawerPromotion
              key={index}
              promotion={promotion}
              drawerIsOpen={drawerIsOpen}
              onClose={onPromotionClose}
              specialOffersVariant={specialOffersVariant}
              showSideDrawerVariant={showSideDrawerVariant}
              applyNewDrawerUI={applyNewDrawerUI}
            />
          ))}
        </section>
      )}
    </Drawer>
  );
};
